
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { authService } from '../services/authService';

// Define admin user type
type AdminUser = {
  id: string;
  username: string;
  role: 'admin' | 'staff' | 'clinic';
};

// Define authentication context type
type AuthContextType = {
  user: AdminUser | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isClinic: boolean;
  hasRole: (role: string) => boolean;
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    const storedUser = authService.getCurrentUser();
    if (storedUser) {
      setUser({
        id: storedUser.id.toString(),
        username: storedUser.username,
        role: storedUser.role as 'admin' | 'staff' | 'clinic'
      });
    }
  }, []);
  
  // Login function
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      // Call auth service to authenticate
      const response = await authService.login(username, password);
      
      // Set user in state
      setUser({
        id: response.user.id.toString(),
        username: response.user.username,
        role: response.user.role as 'admin' | 'staff' | 'clinic'
      });
      
      toast({
        title: 'Login Successful',
        description: 'Welcome to the admin dashboard',
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Login Failed',
        description: error.response?.data?.message || 'Invalid username or password',
        variant: 'destructive',
      });
      return false;
    }
  };
  
  // Logout function
  const logout = () => {
    authService.logout();
    setUser(null);
    navigate('/login');
    toast({
      title: 'Logged Out',
      description: 'You have been successfully logged out',
    });
  };

  // Role checking functions
  const isAdmin = user?.role === 'admin';
  const isStaff = user?.role === 'staff' || user?.role === 'admin';
  const isClinic = user?.role === 'clinic' || user?.role === 'admin';
  
  const hasRole = (role: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true; // Admin has all roles
    return user.role === role;
  };
  
  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout,
      isAuthenticated: user !== null,
      isAdmin,
      isStaff,
      isClinic,
      hasRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Create hook for using the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
